import React, { useState } from "react";
import { Box, Text, Button, Checkbox, Link } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { FaCcVisa, FaUniversity, FaDonate } from "react-icons/fa";
import { BIOFLOW_FEATURES, BioFlowBillingCycle } from "src/types/bioflow";

const paymentMethods = [
  { name: "card", Icon: FaCcVisa, color: "#6c5fe3" },
  { name: "purchase", Icon: FaDonate, color: "#0abf25" },
  { name: "bank", Icon: FaUniversity, color: "#f5b464" },
];

interface BioFlowSubscriptionCardProps {
  billingCycle: BioFlowBillingCycle;
  price: number;
  selected?: string;
  onSelectMethod: (method: string) => void;
  onSubscribe: () => void;
  processing: boolean;
  isActive: boolean;
  disabled?: boolean;
}

const BioFlowSubscriptionCard: React.FC<BioFlowSubscriptionCardProps> = ({
  billingCycle,
  price,
  selected,
  onSelectMethod,
  onSubscribe,
  processing,
  isActive,
  disabled = false,
}) => {
  const [agreeOnTerms, setAgreeOnTerms] = useState(false);
  const intl = useIntl();

  return (
    <Box
      display="flex"
      flexDir="column"
      alignItems="center"
      padding="30px 0"
      borderRadius="15px"
      backgroundColor="white"
      boxShadow="0 2px 10px 2px silver"
      margin={{
        base: "30px 0 !important",
        lg: "30px !important",
      }}
      width={{ base: "300px", md: "350px" }}
      position="relative"
      overflow="hidden"
      opacity={disabled ? 0.6 : 1}
    >
      {isActive && (
        <Box
          position="absolute"
          top="10px"
          left="10px"
          backgroundColor="green.500"
          color="white"
          fontSize="xs"
          fontWeight="bold"
          px={2}
          py={1}
          borderRadius="md"
          textTransform="uppercase"
        >
          <FormattedMessage id="bioflow.status.active" defaultMessage="Active subscription" />
        </Box>
      )}

      <Box padding="0 30px">
        <Text fontSize="36px" fontWeight="bold" color="brand.900">
          <FormattedMessage id="bioflow.plan.name" defaultMessage="SALBioFlow" />
        </Text>
      </Box>

      <Box
        backgroundColor="brand.900"
        padding="30px 0"
        width="100%"
        marginTop="30px"
        maxHeight="100px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Text textAlign="center" color="white" fontSize="28px" fontWeight="bold">
          {price.toFixed(2)}
          <sup>$</sup>
          <Text as="span" fontSize="18px" ml={1}>
            <FormattedMessage
              id={billingCycle === "monthly" ? "askSalah.perMonth" : "askSalah.perYear"}
              defaultMessage={billingCycle === "monthly" ? "/month" : "/year"}
            />
          </Text>
        </Text>
      </Box>

      {!isActive && (
        <Box display="flex" flexWrap="wrap" justifyContent="center" marginTop="30px">
          {paymentMethods.map(({ name, Icon, color }) => (
            <Box
              title={name}
              key={name}
              margin="15px"
              width="80px"
              paddingTop="10px"
              display="flex"
              flexDir="column"
              alignItems="center"
              borderRadius="10px"
              background={name === selected ? "#b2caf7" : "white"}
              onClick={() => {
                if (!disabled) {
                  onSelectMethod(name);
                }
              }}
              cursor={disabled ? "not-allowed" : "pointer"}
              transition="150ms ease-out"
              _hover={{ background: disabled ? "white" : "#b2caf7" }}
            >
              <Icon size={46} height="70px" width="70px" color={color} />
              <Text my={3} fontWeight="600">
                <FormattedMessage id={name} />
              </Text>
            </Box>
          ))}
        </Box>
      )}

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        marginTop="30px"
        padding="0 30px"
        width="100%"
      >
        <Box width="100%" textAlign="left">
          {BIOFLOW_FEATURES.map((feature) => (
            <Text
              key={feature.id}
              fontSize="14px"
              color="gray.600"
              mb={2}
              pl={4}
              _before={{
                content: '"✓"',
                color: "green.500",
                fontWeight: "bold",
                marginRight: "8px",
              }}
            >
              {intl.formatMessage(feature)}
            </Text>
          ))}
        </Box>
      </Box>

      {!isActive && (
        <>
          <Box marginTop="30px">
            {processing && (
              <Box margin="30px 0">
                <Text color="grey">
                  <FormattedMessage id="wait" />
                </Text>
              </Box>
            )}
            <Button
              backgroundColor="brand.900"
              color="white"
              height="50px"
              _hover={{ filter: "brightness(120%)" }}
              onClick={onSubscribe}
              isDisabled={disabled || !selected || processing || !agreeOnTerms}
              isLoading={processing}
              px={8}
            >
              <FormattedMessage id="bioflow.button.subscribe" defaultMessage="Subscribe" />
            </Button>
          </Box>

          <Checkbox
            type="checkbox"
            name="complete"
            isChecked={agreeOnTerms}
            onChange={() => setAgreeOnTerms((prev) => !prev)}
            marginTop="20px"
            isDisabled={disabled}
          >
            <FormattedMessage id="agreeTerms" />{" "}
            <Link
              href="https://app-staging.salbioflow.click/terms"
              isExternal
              color="brand.900"
              textStyle="smallBold"
            >
              <FormattedMessage id="t&c" />
            </Link>
          </Checkbox>
        </>
      )}
    </Box>
  );
};

export default BioFlowSubscriptionCard;
